import { computed, onUnmounted, watch } from 'vue';
import { JobApplicantStatus, ProjectStatus } from '@factoryfixinc/ats-interfaces';
import type { ProjectWithRelations } from '@factoryfixinc/ats-interfaces';
import { ErrorService } from '@/core/shared/errors/error.service';
import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';
import ImmediateOutreachService from './immediate-outreach.service';
import ProjectService from '@/core/shared/project/project.service';

// Create singletons for shared service instances
let sharedImmediateOutreachService: ImmediateOutreachService | null = null;
let sharedConversationIndexService: ConversationIndexService | null = null;
let sharedProjectService: ProjectService | null = null;

/**
 * Composable for managing immediate outreach functionality
 * Provides methods and computed properties for controlling immediate outreach
 */
export function useImmediateOutreach(
  conversationIndexService?: ConversationIndexService,
  projectService?: ProjectService,
): {
  shouldShowImmediateOutreach: ReturnType<typeof computed<boolean>>;
  shouldShowAIAnimation: ReturnType<typeof computed<boolean>>;
  enableImmediateOutreach: (project: ProjectWithRelations<'candidates'>) => void;
  disableImmediateOutreach: (projectId: number) => void;
  startImmediateOutreach: () => void;
  stopImmediateOutreach: () => void;
  immediateOutreachService: ImmediateOutreachService;
} {
  // Initialize or reuse singleton instances to ensure consistency
  if (conversationIndexService) {
    sharedConversationIndexService = conversationIndexService;
  } else if (!sharedConversationIndexService) {
    sharedConversationIndexService = new ConversationIndexService();
  }

  if (projectService) {
    sharedProjectService = projectService;
  } else if (!sharedProjectService) {
    sharedProjectService = new ProjectService();
  }

  // Create or reuse the immediate outreach service with consistent dependencies
  if (!sharedImmediateOutreachService || conversationIndexService) {
    sharedImmediateOutreachService = new ImmediateOutreachService(sharedConversationIndexService);
  }

  const immediateOutreachService = sharedImmediateOutreachService;
  const _projectService = sharedProjectService;
  const _conversationIndexService = sharedConversationIndexService;

  /**
   * Check if current project exists and is valid
   */
  const hasValidProject = computed(() => {
    const currentProject = _projectService.currentProject;
    return !!(currentProject && currentProject.jobId);
  });

  /**
   * Check if immediate outreach is enabled for current project
   */
  const isImmediateOutreachEnabled = computed(() => {
    const currentProject = _projectService.currentProject;
    if (!currentProject || !currentProject.jobId) return false;
    return immediateOutreachService.store.enabledProjectIds.has(currentProject.id);
  });

  /**
   * Check if project meets all eligibility criteria
   * (copilot enabled, active outreach enabled, job is live)
   */
  const isProjectEligible = computed(() => {
    const currentProject = _projectService.currentProject;
    if (!currentProject || !currentProject.jobId) return false;

    const copilotEnabled = currentProject.copilot ?? false;
    const activeOutreachEnabled = currentProject.activeOutreachEnabled ?? false;
    const jobIsLive = currentProject.status === ProjectStatus.LIVE;

    return copilotEnabled && activeOutreachEnabled && jobIsLive;
  });

  /**
   * Check if conversation filters are set up for outreach
   * (CONTACT status filter selected and job is selected)
   */
  const isConversationFilterReady = computed(() => {
    const selectedStatusList = _conversationIndexService.selectedStatusList;
    const hasContactFilter = selectedStatusList?.includes(JobApplicantStatus.CONTACT) ?? false;
    const hasJobSelected = _conversationIndexService.conversationIndexSearch.jobIds.length > 0;

    return hasContactFilter && hasJobSelected;
  });

  /**
   * Check if there are no candidates in CONTACT status
   */
  const hasNoContactCandidates = computed(() => {
    const currentProject = _projectService.currentProject;
    if (!currentProject || !currentProject.jobId) return false;

    if (!currentProject.candidates) return true;

    const contactCount =
      (currentProject.candidates as Record<string, number>)[JobApplicantStatus.CONTACT] ?? 0;

    return contactCount === 0;
  });

  /**
   * Check if all conditions are met for showing immediate outreach
   * Combines all the granular checks into the final decision
   */
  const shouldShowImmediateOutreach = computed(() => {
    try {
      return (
        hasValidProject.value &&
        isImmediateOutreachEnabled.value &&
        isProjectEligible.value &&
        isConversationFilterReady.value &&
        hasNoContactCandidates.value
      );
    } catch (error) {
      ErrorService.captureException(error);
      return false;
    }
  });

  /**
   * Check if the AI animation should be shown
   * Animation shows when conditions are met AND polling is active
   */
  const shouldShowAIAnimation = computed(() => {
    return shouldShowImmediateOutreach.value && immediateOutreachService.store.pollingActive;
  });

  /**
   * Enable immediate outreach for a project if activeOutreachEnabled is true
   */
  const enableImmediateOutreach = (project: ProjectWithRelations<'candidates'>): void => {
    if (project.activeOutreachEnabled) {
      immediateOutreachService.enableForProject(project.id);
    }
  };

  /**
   * Disable immediate outreach for a project
   */
  const disableImmediateOutreach = (projectId: number): void => {
    immediateOutreachService.disableForProject(projectId);
  };

  /**
   * Start immediate outreach polling
   */
  const startImmediateOutreach = (): void => {
    immediateOutreachService.startImmediateOutreach();
  };

  /**
   * Stop immediate outreach polling
   */
  const stopImmediateOutreach = (): void => {
    immediateOutreachService.stopImmediateOutreach();
  };

  // Watch for condition changes to start/stop polling
  const stopWatcher = watch(
    shouldShowImmediateOutreach,
    (shouldShow) => {
      if (shouldShow) {
        startImmediateOutreach();
      } else {
        stopImmediateOutreach();
      }
    },
    { immediate: true }, // Run immediately with current value
  );

  // Clean up on unmount
  onUnmounted(() => {
    stopWatcher();
    stopImmediateOutreach();
  });

  return {
    // Computed properties
    shouldShowImmediateOutreach,
    shouldShowAIAnimation,

    // Methods
    enableImmediateOutreach,
    disableImmediateOutreach,
    startImmediateOutreach,
    stopImmediateOutreach,

    // Service instance (if needed for direct access)
    immediateOutreachService,
  };
}
